// CompanyReviewsPage — модерация UGC: отзывы сотрудников о компаниях (migration 141)
// и комментарии к статьям блога (migration 142). Публично отзыв виден только после approve здесь —
// до модерации это юридический/репутационный риск, не просто UX-гейт.
import React, { useCallback, useEffect, useState } from 'react';
import { Star, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface PendingReview {
  id: number;
  company_id: number;
  company_name: string;
  user_id: number;
  role_title: string;
  rating: number;
  body: string;
  created_at: string;
}

interface PendingComment {
  id: number;
  article_slug: string;
  user_id: number;
  author_name: string;
  body: string;
  created_at: string;
}

const CompanyReviewsPage: React.FC = () => {
  const [comments, setComments] = useState<PendingComment[]>([]);
  const [commentBusy, setCommentBusy] = useState<number | null>(null);
  const [commentRejecting, setCommentRejecting] = useState<number | null>(null);
  const [commentReason, setCommentReason] = useState('');

  const [reviews, setReviews] = useState<PendingReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/company-reviews')
      .then((resp) => setReviews(resp.data?.reviews || []))
      .catch(() => toast.error('Не удалось загрузить отзывы'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/admin/blog-comments')
      .then((resp) => setComments(resp.data?.comments || []))
      .catch(() => toast.error('Не удалось загрузить комментарии'));
  }, []);

  const moderateComment = async (id: number, status: 'approved' | 'rejected', rejectReason?: string) => {
    setCommentBusy(id);
    try {
      await api.post(`/admin/blog-comments/${id}/moderate`, { status, reason: rejectReason || '' });
      setComments((prev) => prev.filter((c) => c.id !== id));
      toast.success(status === 'approved' ? 'Комментарий опубликован' : 'Комментарий отклонён');
      setCommentRejecting(null);
      setCommentReason('');
    } catch {
      toast.error('Не удалось обновить комментарий');
    } finally {
      setCommentBusy(null);
    }
  };

  const moderate = async (id: number, status: 'approved' | 'rejected', rejectReason?: string) => {
    setBusyId(id);
    try {
      await api.post(`/admin/company-reviews/${id}/moderate`, { status, reason: rejectReason || '' });
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success(status === 'approved' ? 'Отзыв опубликован' : 'Отзыв отклонён');
      setRejectingId(null);
      setReason('');
    } catch {
      toast.error('Не удалось обновить отзыв');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Star className="h-6 w-6 text-gray-700" />
        <h1 className="text-xl font-semibold text-gray-900">Модерация отзывов о компаниях</h1>
        <span className="ml-2 text-sm text-gray-500">{reviews.length} на рассмотрении</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Загрузка…
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-gray-500">Очередь пуста — новых отзывов на модерацию нет.</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-gray-900">{r.company_name || `Компания #${r.company_id}`}</div>
                  <div className="text-sm text-gray-500">{r.role_title} · user #{r.user_id} · {new Date(r.created_at).toLocaleDateString('ru-RU')}</div>
                </div>
                <div className="flex text-amber-400 flex-shrink-0">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={16} fill={n <= r.rating ? 'currentColor' : 'none'} />
                  ))}
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">{r.body}</p>

              {rejectingId === r.id ? (
                <div className="mt-3 flex gap-2">
                  <input
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Причина отклонения"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    autoFocus
                  />
                  <button
                    className="px-3 py-2 text-sm bg-red-600 text-white rounded-md disabled:opacity-50"
                    disabled={!reason.trim() || busyId === r.id}
                    onClick={() => moderate(r.id, 'rejected', reason.trim())}
                  >
                    Отклонить
                  </button>
                  <button
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    onClick={() => { setRejectingId(null); setReason(''); }}
                  >
                    Отмена
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <button
                    className="flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-md disabled:opacity-50"
                    disabled={busyId === r.id}
                    onClick={() => moderate(r.id, 'approved')}
                  >
                    <Check size={15} /> Опубликовать
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                    disabled={busyId === r.id}
                    onClick={() => setRejectingId(r.id)}
                  >
                    <X size={15} /> Отклонить
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mt-10 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Комментарии к статьям блога</h2>
        <span className="text-sm text-gray-500">{comments.length} на рассмотрении</span>
      </div>
      {comments.length === 0 ? (
        <p className="text-gray-500">Очередь пуста — новых комментариев нет.</p>
      ) : (
        <div className="space-y-4">
          {comments.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-500">
                {c.author_name} · user #{c.user_id} · /blog/{c.article_slug} · {new Date(c.created_at).toLocaleString('ru-RU')}
              </div>
              <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{c.body}</p>
              {commentRejecting === c.id ? (
                <div className="mt-3 flex gap-2">
                  <input
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Причина отклонения"
                    value={commentReason}
                    onChange={(e) => setCommentReason(e.target.value)}
                    autoFocus
                  />
                  <button
                    className="px-3 py-2 text-sm bg-red-600 text-white rounded-md disabled:opacity-50"
                    disabled={!commentReason.trim() || commentBusy === c.id}
                    onClick={() => moderateComment(c.id, 'rejected', commentReason.trim())}
                  >
                    Отклонить
                  </button>
                  <button className="px-3 py-2 text-sm border border-gray-300 rounded-md" onClick={() => { setCommentRejecting(null); setCommentReason(''); }}>
                    Отмена
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <button
                    className="flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-md disabled:opacity-50"
                    disabled={commentBusy === c.id}
                    onClick={() => moderateComment(c.id, 'approved')}
                  >
                    <Check size={15} /> Опубликовать
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                    disabled={commentBusy === c.id}
                    onClick={() => setCommentRejecting(c.id)}
                  >
                    <X size={15} /> Отклонить
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyReviewsPage;
